import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirm } from "material-ui-confirm";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import WidgetTemplate from "../../widgets/WidgetTemplate";
import { RootState } from "../../../app/store";
import { commandActions } from "../../../redux/command/commandSlice";
import { toDate } from "../../../utils/TimeUtils";
import { RemoteDevice, deviceState } from "./CnesCommandTab";
import { Device } from "./DeviceCard";

const GraphWidget = React.memo((props: any) => {

    const dispatch = useDispatch();
    const confirm = useConfirm();
    const { id, gridId, reduced, params, width, height } = props;

    const commands = useSelector((state: RootState) => state.command.commands);
    const commandHistory = Object.keys(commands).map(key => commands[key]).filter(command => command.source === params.source);

    const frame = useSelector((state: RootState) => {
        const currentTime = state.replay.currentTime;
        const currentTS = toDate(currentTime).getTime() / 1000;

        const stream = state.telemetry.streams[params.source]?.[params.label];
        const currentTimestamps = stream ?
            currentTS ?
                stream[0].filter(timestamp => timestamp <= currentTS)
            : stream[0]
        : null;
        const currentStream = currentTimestamps ? [currentTimestamps, stream[1].slice(0, currentTimestamps.length)] : null;

        const frame = currentStream?.[1] ? currentStream[1].slice(-1)[0] : null; // getting last data frame from stream
        
        return frame;
    });

    const [device, setDevice] = useState<RemoteDevice>(deviceState(params.kind, frame, commandHistory, params.device.id, params.device.name, params.source));

    useEffect(() => {
            const history = Object.keys(commands).map(key => commands[key]).filter(command => command.source === params.source);
            // ^to prevent re-render loop tiggered by 'commandHistory' redefinition on each render
            setDevice(deviceState(params.kind, frame, history, device.id, device.name, params.source));
        }, [commands, params, frame, device.id, device.name]);

    const relayCommand = useCallback((destination: string, relayId: string, state: number) => {
        confirm({
            confirmationButtonProps: {color: "inherit"},
            cancellationButtonProps: {color: "inherit"},
            description: `Set '${relayId}' to ${state ? "'NO'" : "'NC'"} ?`
        }).then(() => {
            dispatch(commandActions.send_relay_command({destination, relayId, state}));
        });
    }, [confirm, dispatch]);

    const buzzerCommand = useCallback((destination: string, buzzerId: string, frequency: number) => {
        confirm({
            confirmationButtonProps: {color: "inherit"},
            cancellationButtonProps: {color: "inherit"},
            description: `Turn the buzzer ${frequency ? "on" : "off"} ?`
        }).then(() => {
            dispatch(commandActions.send_buzzer_command({destination, buzzerId, frequency}));
        });
    }, [confirm, dispatch]);

    const missingCommand = () => console.log(`Unknown device type : ${params.kind}`);

    const deviceCommand = params.kind === "relay" ?
        relayCommand
    : params.kind === "buzzer" ?
        buzzerCommand
    : missingCommand

    return (
        <WidgetTemplate
            id={id}
            gridId={gridId}
            header={`${params.name} - ${params.device.name}`}
            actions={[]}
            width={width}
            height={height}
            reduced={reduced}
        >
            {frame ?
                <Box sx={{padding: 2, minWidth: 200, minHeight: params.kind === "relay" ? 280 : 150, maxHeight: "90%", width, height}}>
                    <Device kind={params.kind} device={device} deviceCommand={deviceCommand} commandHistory={commandHistory} width={width} isWidget={true}/>
                </Box>
            : <Typography variant="h6" color="text.secondary" sx={{marginTop: 1, marginLeft: 2, maxHeight: "90%", maxWidth: "90%"}}>No data available</Typography>
            }
        </WidgetTemplate>
    );
});

export default GraphWidget;